/**
 * Funding Arbitrage — Beta
 * Delta-neutral: long on exchange A + short on exchange B.
 * Sim mode by default; live mode requires explicit toggle.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import HedgeCompleteModal, { HedgePrefill } from '../components/HedgeCompleteModal';
import ScaleModal, { ScalePrefill } from '../components/ScaleModal';
import LegOrderModal, { LegOrderPrefill } from '../components/LegOrderModal';
import { useWS } from '../WSContext';
import { getTheme } from '../App';
import {
  RefreshCw, X, ChevronUp, ChevronDown, Search,
  AlertTriangle, Play, Square, Info,
} from 'lucide-react';

interface Opportunity {
  symbol:         string;
  long_bot:       string;
  long_bot_name:  string;
  long_sym:       string;
  long_funding:   number;
  long_mark:      number;
  long_bid:       number;
  long_ask:       number;
  short_bot:      string;
  short_bot_name: string;
  short_sym:      string;
  short_funding:  number;
  short_mark:     number;
  short_bid:      number;
  short_ask:      number;
  net_funding:    number;
}

interface ArbPair {
  id:                  string;
  symbol:              string;
  long_bot:            string;
  long_bot_name:       string;
  long_sym:            string;
  short_bot:           string;
  short_bot_name:      string;
  short_sym:           string;
  size:                number;
  leverage:            number;
  long_entry:          number;
  short_entry:         number;
  opened_at:           number;
  long_funding_open:   number;
  short_funding_open:  number;
  status:              'active' | 'closing' | 'closed';
  sim_mode:            boolean;
  long_mark_now:       number;
  short_mark_now:      number;
  long_funding_now:    number;
  short_funding_now:   number;
  sim_pnl_mark:        number;
  sim_funding_est:     number;
  delta_usd?:          number;
}

// ── helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number, d = 4) {
  return n.toFixed(d);
}

// est_funding zaten % cinsinden saklanıyor (ör. 0.0437 → %0.0437)
function fmtPct(n: number, decimals = 4) {
  const p = n.toFixed(decimals);
  return `${n >= 0 ? '+' : ''}${p}%`;
}

function age(ts: number) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60)  return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}

// ── BidAskCell ───────────────────────────────────────────────────────────────

// 2.4K / 1.2M gibi kısalt — kolon dar, tam sayı okunmuyor
function qtyFmt(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e6) return (n / 1e6).toFixed(a >= 1e7 ? 0 : 1) + 'M';
  if (a >= 1e3) return (n / 1e3).toFixed(a >= 1e4 ? 0 : 1) + 'K';
  return n.toLocaleString('en-US', { maximumFractionDigits: a < 1 ? 4 : 0 });
}

function BidAskCell({ bid, ask, bidQty, askQty, dark }:
  { bid: number; ask: number; bidQty?: number; askQty?: number; dark: boolean }) {
  if (!bid && !ask) return <span className="text-slate-500 text-[10px]">—</span>;
  const spread    = ask - bid;
  const spreadPct = bid > 0 ? (spread / bid) * 100 : 0;
  // Spread rengi: <0.02% yeşil, <0.05% sarı, üzeri kırmızı
  const spreadColor = spreadPct < 0.02 ? 'text-emerald-400' : spreadPct < 0.05 ? 'text-amber-400' : 'text-rose-400';
  // Ondalık sayısı BÜYÜKLÜKTEN değil, SPREAD'i görünür kılmaktan türetiliyor.
  // Sabit kural (0.01 üstü → 4 hane) PNUT gibi 0.0417 seviyesindeki paritelerde
  // bid ve ask'i aynı gösteriyordu: %0.024'lük gerçek fark ekranda kayboluyordu.
  const priceDecimals = (() => {
    const base = bid >= 1000 ? 1 : bid >= 100 ? 2 : bid >= 1 ? 3 : bid >= 0.01 ? 4 : 6;
    if (!(spread > 0)) return base;
    // Farkı en az bir anlamlı basamakla göstermeye yetecek kadar aç
    const needed = Math.ceil(-Math.log10(spread)) + 1;
    return Math.min(Math.max(base, needed), 8);
  })();

  return (
    <div className="flex flex-col gap-0.5 text-[11px]">
      {/* Miktar: fiyat cazip ama derinlik yoksa o satır uygulanamaz.
          Veremeyen borsalarda (ör. Hyperliquid impactPxs) sessizce boş kalır. */}
      <div className="flex items-center gap-1.5">
        <span className="text-emerald-500 w-5">B</span>
        <span className={dark ? 'text-white' : 'text-slate-800'}>{bid.toFixed(priceDecimals)}</span>
        {bidQty ? <span className="text-slate-500 text-[10px]">×{qtyFmt(bidQty)}</span> : null}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-rose-400 w-5">A</span>
        <span className={dark ? 'text-white' : 'text-slate-800'}>{ask.toFixed(priceDecimals)}</span>
        {askQty ? <span className="text-slate-500 text-[10px]">×{qtyFmt(askQty)}</span> : null}
      </div>
      <div className={`${spreadColor} text-[10px]`}>
        Δ {spreadPct.toFixed(3)}%
      </div>
    </div>
  );
}

// ── OpenPairModal ─────────────────────────────────────────────────────────────

interface OpenPairModalProps {
  dark: boolean;
  opp: Opportunity;
  onClose: () => void;
  onOpen: (payload: object) => void;
}

function OpenPairModal({ dark, opp, onClose, onOpen }: OpenPairModalProps) {
  const t = getTheme(dark);
  const [size,     setSize]     = useState('10');
  const [leverage, setLeverage] = useState('3');
  const [simMode,  setSimMode]  = useState(true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onOpen({
      symbol:    opp.symbol,
      long_bot:  opp.long_bot,
      long_sym:  opp.long_sym,
      short_bot: opp.short_bot,
      short_sym: opp.short_sym,
      size:      parseFloat(size),
      leverage:  parseInt(leverage),
      sim_mode:  simMode,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className={`${t.card} border ${t.cardBorder} rounded-2xl p-6 w-[400px] shadow-2xl`}>
        <div className="flex items-center justify-between mb-5">
          <h3 className={`font-bold text-base ${t.text}`}>Pair Aç — {opp.symbol}</h3>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X size={18} /></button>
        </div>

        {/* Opportunity summary */}
        <div className={`rounded-xl p-3 mb-5 text-xs ${dark ? 'bg-[#0a2560]/60' : 'bg-[#E8EEF7]'} space-y-1.5`}>
          <div className="flex justify-between">
            <span className={t.muted}>Long (düşük funding)</span>
            <span className={`${t.text} font-medium`}>{opp.long_bot_name} · {opp.long_sym}</span>
          </div>
          <div className="flex justify-between">
            <span className={t.muted}>Short (yüksek funding)</span>
            <span className={`${t.text} font-medium`}>{opp.short_bot_name} · {opp.short_sym}</span>
          </div>
          <div className="flex justify-between">
            <span className={t.muted}>Net Funding / 8h</span>
            <span className="text-emerald-400 font-semibold">{fmtPct(opp.net_funding)}</span>
          </div>
          <div className="flex justify-between">
            <span className={t.muted}>Yıllık tahmin</span>
            <span className="text-emerald-400 font-semibold">
              {fmtPct(opp.net_funding * 3 * 365)} / yıl
            </span>
          </div>
          <div className="flex justify-between items-start pt-1 border-t border-slate-700/40">
            <span className={t.muted}>Long giriş (maker bid)</span>
            <span className={`${t.text} font-mono text-xs`}>
              {opp.long_bid.toFixed(opp.long_bid >= 100 ? 2 : 4)}
              <span className="text-slate-500 ml-1">/ ask {opp.long_ask.toFixed(opp.long_ask >= 100 ? 2 : 4)}</span>
            </span>
          </div>
          <div className="flex justify-between items-start">
            <span className={t.muted}>Short giriş (maker ask)</span>
            <span className={`${t.text} font-mono text-xs`}>
              {opp.short_ask.toFixed(opp.short_ask >= 100 ? 2 : 4)}
              <span className="text-slate-500 ml-1">/ bid {opp.short_bid.toFixed(opp.short_bid >= 100 ? 2 : 4)}</span>
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block text-xs font-medium mb-1 ${t.muted}`}>Pozisyon Büyüklüğü (kontrat)</label>
            <input
              type="number" step="any" min="0.001" required
              value={size} onChange={e => setSize(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border text-sm ${t.inp} outline-none`}
            />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${t.muted}`}>Kaldıraç</label>
            <input
              type="number" step="1" min="1" max="200" required
              value={leverage} onChange={e => setLeverage(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border text-sm ${t.inp} outline-none`}
            />
          </div>

          {/* Sim mode toggle */}
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              onClick={() => setSimMode(!simMode)}
              className={`w-11 h-6 rounded-full relative transition-colors ${simMode ? 'bg-blue-500' : 'bg-rose-500'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${simMode ? 'translate-x-0.5' : 'translate-x-5'}`} />
            </div>
            <span className={`text-xs font-medium ${simMode ? 'text-blue-400' : 'text-rose-400'}`}>
              {simMode ? 'Simülasyon Modu (emir atılmaz)' : 'LIVE MOD — gerçek emir atılır!'}
            </span>
          </label>

          {!simMode && (
            <div className={`flex items-start gap-2 px-3 py-2 rounded-lg ${dark ? 'bg-rose-900/30 border border-rose-700/40' : 'bg-rose-50 border border-rose-300'}`}>
              <AlertTriangle size={14} className="text-rose-400 mt-0.5 shrink-0" />
              <p className="text-xs text-rose-400">
                Live modda gerçek emir atılır. Her iki bacak da aynı anda açılır; bacak riski varsa otomatik unwind yapılır.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className={`flex-1 px-4 py-2 rounded-xl text-sm font-medium ${t.btnSecondary}`}>
              İptal
            </button>
            <button type="submit"
              className={`flex-1 px-4 py-2 rounded-xl text-sm font-medium ${simMode ? t.btnPrimary : 'bg-rose-600 hover:bg-rose-700 text-white'}`}>
              {simMode ? 'Simüle Et' : 'Canlı Aç'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

// Borsa sembolünden çıplak coin adı. Backend'deki canonical() ile aynı sırayı
// izler: önce PERP_ öneki, sonra -PERP soneki, en son teminat soneki.
// Sonraki funding'e kalan süre. Borsa alanı vermiyorsa (Extended, Hyperliquid)
// TÜRETMİYORUZ — "saat başıdır" varsayımı tahmini gerçek gibi gösterirdi.
const FundingIn: React.FC<{ ts: number; muted: string }> = ({ ts, muted }) => {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!ts) return <span className={muted}>funding saati —</span>;
  const left = ts - now;
  if (left <= 0) return <span className="text-amber-400">funding şimdi</span>;
  const h = Math.floor(left / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const sec = Math.floor((left % 60000) / 1000);
  const t = new Date(ts);
  const hhmm = `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
  return (
    <span className={left < 300000 ? 'text-amber-400' : muted} title={`sonraki funding ${t.toLocaleString()}`}>
      {hhmm} · {h > 0 ? `${h}s ` : ''}{m}dk{h === 0 && m < 10 ? ` ${sec}sn` : ''}
    </span>
  );
};

// Margin ödünç stoku. Binance'in İLAN ETTİĞİ kullanıcı limiti değil, havuzda o an
// gerçekten ödünç verilebilir miktar — spot-perp kurgusunda tek belirleyici sayı.
// Funding derin negatifken herkes aynı yöne koştuğu için havuz fırsat anında boşalıyor;
// sıfır görmek istisna değil, kural.
const BorrowAvail: React.FC<{ amount: any; rate: any; base: string; muted: string }> =
  ({ amount, rate, base, muted }) => {
  if (amount == null) return null;
  const v = Number(amount) || 0;
  const r = rate == null ? null : Number(rate);      // GÜNLÜK yüzde
  const fmt = v >= 1000 ? v.toLocaleString('en-US', { maximumFractionDigits: 0 })
                        : v.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return (
    <div className={`text-[10px] ${v > 0 ? muted : 'text-rose-400/80'}`}
      title={v > 0 ? 'Binance margin havuzunda ödünç alınabilir miktar ve günlük faizi'
                   : 'Havuz boş — bu varlık şu an ödünç alınamaz'}>
      Available: {fmt} {base}
      {v > 0 && r != null && (
        <span title={`yıllık ~%${(r * 365).toFixed(0)} — funding ile aynı birimde kıyasla`}>
          {' · '}faiz %{r.toFixed(4)}/gün
        </span>
      )}
    </div>
  );
};

const canonicalBase = (symbol: string): string => {
  let x = String(symbol || '').toUpperCase().replace('PERP_', '');
  x = x.replace(/[-_]?PERP$/, '');
  x = x.replace(/[-_]?(USDC|USDT|USD)$/, '');
  return x;
};

export default function FundingArb({ dark }: { dark: boolean }) {
  const t = getTheme(dark);
  const { sendWs, subscribe, sendForBot, botsState } = useWS();

  const [opps,       setOpps]       = useState<Opportunity[]>([]);
  // Cross-exchange funding arb grupları — işaretli trade'lerden türetilen gerçekleşmiş sonuç
  const [perpGroups, setPerpGroups] = useState<Record<string, any>>({});
  const [perpList,   setPerpList]   = useState<any[]>([]);
  const [gTab,       setGTab]       = useState<'living' | 'closed'>('living');
  const [pairs,      setPairs]       = useState<ArbPair[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [openModal,  setOpenModal]  = useState<Opportunity | null>(null);
  const [tab,        setTab]        = useState<'scanner' | 'active' | 'groups'>('scanner');
  // Varsayılan görünüm ODAKLI: yalnız hem funding hem basis'te kazandıran çiftler.
  // Tam liste istenirse anahtarla açılır.
  const [onlyBoth,   setOnlyBoth]   = useState(true);
  // Kapatılan borsalar. Bir çiftin İKİ bacağından biri kapalı borsadaysa fırsat gizlenir.
  const [offVenues,  setOffVenues]  = useState<Set<string>>(new Set());
  const [query,      setQuery]      = useState('');
  // Kapat tuşuna basılınca önce canlı kotasyon isteniyor; cevap gelince popup açılıyor.
  const [closeReq,   setCloseReq]   = useState<any>(null);
  const [hedge,      setHedge]      = useState<HedgePrefill | null>(null);
  const [scale,      setScale]      = useState<ScalePrefill | null>(null);
  const [legOrder,   setLegOrder]   = useState<LegOrderPrefill | null>(null);
  // Varsayılan sıralama exec_net_bps — İCRA EDİLEBİLİR net fark. Eskiden basis_edge
  // ile sıralanıyordu; o sayı oracle ayrışmasını kâr gibi gösterip listenin başına
  // hiç girilemeyecek satırları taşıyordu (ONE: edge %+0,79 iken icra −11 bps).
  // Eski not: aradığımız şey öncelikle borsalar arası fiyat
  // farkı; funding onun üstüne gelen tekrarlayan gelir.
  const [sortField,  setSortField]  = useState<string>('exec_net_bps');
  const [sortDir,    setSortDir]    = useState<'asc' | 'desc'>('desc');
  const [showClosed, setShowClosed] = useState(false);

  // Subscribe to WS messages
  useEffect(() => {
    return subscribe((data: any) => {
      if (data.type === 'arb_opportunities') {
        setOpps(data.payload || []);
        setLoading(false);
      }
      if (data.type === 'arb_pairs') {
        setPairs(data.payload || []);
      }
      if (data.type === 'leg_quote') {
        setCloseReq((req: any) => {
          if (!req || data.bot_id !== req.botId) return req;
          const q = data.payload || {};
          if (!q.found) { alert(`${req.symbol} için canlı fiyat alınamadı.`); return null; }
          // Kapatma yönü açık pozisyonun TERSİ. Taker karşıyı geçer (long kapatmak
          // için satış → bid), maker kendi tarafına yazılır (satış → ask).
          const closeSide: 'long' | 'short' = req.openSide === 'long' ? 'short' : 'long';
          const isBuy = closeSide === 'long';
          const price = req.maker
            ? (isBuy ? q.best_bid : q.best_ask)
            : (isBuy ? q.best_ask : q.best_bid);
          setLegOrder({
            botId: req.botId, botName: req.botId, symbol: req.symbol,
            side: closeSide, price: Number(price) || 0,
            availQty: Number(isBuy ? q.best_ask_qty : q.best_bid_qty) || 0,
            makerFee: q.maker_fee, takerFee: q.taker_fee, feeSource: q.fee_source,
            closing: true, groupNo: req.groupNo, openSide: req.openSide,
            openQty: req.openQty, posSide: q.pos_side, posSize: q.pos_size,
            ageS: q.age_s,
            maker: req.maker,
          } as any);
          return null;
        });
      }
      if (data.type === 'perp_groups') {
        setPerpGroups(data.payload?.groups || {});
        setPerpList(data.payload?.list || []);
      }
      if (data.type === 'lp_group_created' || data.type === 'lp_group_trades_saved' ||
          data.type === 'lp_group_status_saved') {
        sendWs({ type: 'get_perp_groups', payload: {} });
      }
    });
  }, [subscribe, sendWs]);

  // Gruplar sekmesi açılınca (ve ilk yüklemede) gerçekleşmiş sonuçları çek
  useEffect(() => { sendWs({ type: 'get_perp_groups', payload: {} }); }, [sendWs]);

  // ── Grup kartlarını AKAN veriyle tazele ───────────────────────────────────
  // `get_perp_groups` yalnız açılışta ve grup değişince geliyor — periyodik
  // yenileme hiç yoktu, uPnL sayfa açıldığı anda donuyordu. Sunucuya yeni bir
  // istek eklemek yerine zaten saniyede bir gelen yayınları kullanıyoruz:
  // `positions` (5 sn) ve `tickers` (3 sn) her ikisi de bot_id etiketli.
  //
  // Yalnız mark fiyatına BAĞLI olan kısım yeniden hesaplanıyor. Dolum olmadıkça
  // değişmeyen her şey (openAvg, openQty, gerçekleşmiş PnL, komisyon, funding)
  // sunucudan geldiği gibi duruyor.
  const livePerpGroups = useMemo(() => {
    // Mark için ÖNCE pozisyon: sunucudaki _own_open_pnl de pozisyonun markPrice'ını
    // kullanıyor, aynı kaynaktan okuyunca iki taraf birbirini tutuyor. Ticker
    // yedek — pozisyon listesi bir tur geç gelirse boşluğu kapatır.
    // Tazelik sırası: borsa websocket akışı (saniye altı) → pozisyon turu (5 sn)
    // → ticker turu (3 sn) → sunucunun açılışta verdiği fiyat. Akışı olmayan
    // borsalarda (Bluefin, Extended, KuCoin, Orderly) ilk basamak boş kalır ve
    // davranış eskisiyle aynı olur — kimse eski hâlinden kötü duruma düşmez.
    const markOf = (botId: string, symbol: string): number => {
      const bs: any = (botsState as any)?.[botId];
      if (!bs) return 0;
      const live = Number(bs.marks?.[symbol] || 0);
      if (live > 0) return live;
      const p = (bs.positions || []).find((x: any) => x.symbol === symbol);
      if (Number(p?.markPrice) > 0) return Number(p.markPrice);
      const tk = (bs.tickers || []).find((x: any) => x.symbol === symbol);
      return Number(tk?.mark_price || tk?.index_price || 0);
    };
    const out: Record<string, any> = {};
    for (const [no, g] of Object.entries(perpGroups as Record<string, any>)) {
      if (!g || !Array.isArray(g.legs)) { out[no] = g; continue; }
      // netUsd sunucuda openPnl'i ZATEN İÇERİYOR (server.py: netUsd + openPnl).
      // Tabanı çıkarmadan yenisini eklersek kağıt PnL iki kez sayılır.
      const baseNet = Number(g.netUsd || 0) - Number(g.openPnl || 0);
      const legs = g.legs.map((l: any) => {
        const oq = Number(l.openQty || 0);
        const oa = Number(l.openAvg || 0);
        const mark = markOf(l.botId, l.symbol) || Number(l.markPrice || 0);
        // Sunucudaki formülün aynısı: (mark − ortalama) × açık miktar.
        // oq short'ta negatif → işaret kendiliğinden doğru.
        const openPnl = (oq && oa > 0 && mark > 0) ? (mark - oa) * oq : 0;
        return { ...l, markPrice: mark || Number(l.markPrice || 0), openPnl };
      });
      const openPnl = legs.reduce((a: number, l: any) => a + Number(l.openPnl || 0), 0);
      out[no] = { ...g, legs, openPnl, netUsd: baseNet + openPnl };
    }
    return out;
  }, [perpGroups, botsState]);

  const fetchOpps = useCallback(() => {
    setLoading(true);
    sendWs({ type: 'get_arb_opportunities' });
  }, [sendWs]);

  // Auto-fetch on mount
  useEffect(() => { fetchOpps(); }, [fetchOpps]);

  const handleOpen = (payload: object) => {
    sendWs({ type: 'open_arb_pair', payload });
  };

  const handleClose = (pair_id: string) => {
    sendWs({ type: 'close_arb_pair', payload: { pair_id } });
  };

  // Sorting
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };
  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <span className="opacity-20"><ChevronUp size={12} /></span>;
    return sortDir === 'desc' ? <ChevronDown size={12} className="text-[#4A9FE8]" /> : <ChevronUp size={12} className="text-[#4A9FE8]" />;
  };

  const sortedOpps = [...opps].sort((a: any, b: any) => {
    const v = sortDir === 'asc' ? 1 : -1;
    // basis_edge oracle/derinlik yoksa null olabiliyor; null'ları HER ZAMAN sona at,
    // yoksa sıralama yönüne göre başa çıkıp listeyi işe yaramaz hale getiriyorlar.
    const av = a[sortField], bv = b[sortField];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return av < bv ? -v : av > bv ? v : 0;
  });

  const activePairs = pairs.filter(p => p.status !== 'closed');
  const closedPairs = pairs.filter(p => p.status === 'closed');
  const displayPairs = showClosed ? pairs : activePairs;

  // Borsa listesi fırsatlardan türetilir (id → görünen ad); sabit liste tutmuyoruz ki
  // yeni bir bot eklenince kendiliğinden görünsün.
  // Aynı coin'in sembolü her borsada başka: PERP_PUMP_USDC (Orderly) · PUMP-USD
  // (Extended) · PUMPUSDT (Binance). Hedge modal'ında borsa değiştirilebiliyor ama
  // sembol karşı bacaktan geldiği gibi kalıyordu → hedef borsa pariteyi tanımıyor,
  // boş defter dönüyor, motor sessizce boşa dönüyordu. Burada çeviriyoruz.
  const symbolFor = useMemo(() => {
    const m = new Map<string, string>();          // `${botId}|${base}` → borsa sembolü
    for (const o of opps as any[]) {
      if (o.long_bot)  m.set(`${o.long_bot}|${o.symbol}`,  o.long_sym);
      if (o.short_bot) m.set(`${o.short_bot}|${o.symbol}`, o.short_sym);
    }
    return (botId: string, base: string) => m.get(`${botId}|${base}`) || '';
  }, [opps]);

  const venues = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of opps as any[]) {
      if (o.long_bot)  m.set(o.long_bot,  o.long_bot_name  || o.long_bot);
      if (o.short_bot) m.set(o.short_bot, o.short_bot_name || o.short_bot);
    }
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [opps]);

  // Arama: boşluk/virgülle ayrılmış terimlerden HERHANGİ biri tutarsa satır kalır
  // ("pump deep" ikisini birden gösterir). Çıplak coin adının yanında borsa
  // sembolleri de taranıyor — "PERP_PUMP_USDC" ya da "PUMP-USD" yazmak da doğal.
  const terms = useMemo(
    () => query.toLowerCase().split(/[\s,]+/).filter(Boolean), [query]);
  const queryOpps = useMemo(() => terms.length === 0 ? sortedOpps
    : sortedOpps.filter((o: any) => {
        const hay = `${o.symbol} ${o.long_sym} ${o.short_sym}`.toLowerCase();
        return terms.some(w => hay.includes(w));
      }), [sortedOpps, terms]);

  // Önce arama, sonra borsa süzgeci, sonra odak süzgeci — sayaçlar da bu sıraya göre.
  const venueOpps = queryOpps.filter(
    (o: any) => !offVenues.has(o.long_bot) && !offVenues.has(o.short_bot));
  // basis_edge null ise (oracle/derinlik yok) odak filtresi DIŞARIDA bırakır — uydurma yapmıyoruz.
  const shownOpps = onlyBoth ? venueOpps.filter((o: any) => o.both_win) : venueOpps;
  const bothCount = venueOpps.filter((o: any) => o.both_win).length;
  const toggleVenue = (id: string) => setOffVenues(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const thCls = `px-3 py-2 text-left text-xs font-semibold border-b ${t.tableHeader} cursor-pointer select-none whitespace-nowrap`;

  return (
    <div className={`flex flex-col h-full ${t.bg} ${t.text}`}>
      {/* Header */}
      <div className={`shrink-0 flex items-center justify-between px-6 py-4 border-b ${t.border}`}>
        <div>
          <h2 className={`font-bold text-lg ${t.text}`}>Funding Arbitraj <span className="text-xs font-normal text-amber-400 ml-2 border border-amber-400/40 px-2 py-0.5 rounded-full">BETA</span></h2>
          <p className={`text-xs ${t.muted} mt-0.5`}>Delta-nötral: yüksek funding'de short, düşükte long — net funding geliri.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchOpps}
            disabled={loading}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${t.btnSecondary} disabled:opacity-50`}>
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Tara
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={`shrink-0 flex border-b ${t.border} px-6`}>
        {(['scanner', 'active', 'groups'] as const).map(tab_ => (
          <button
            key={tab_}
            onClick={() => setTab(tab_)}
            className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
              tab === tab_
                ? 'border-[#4A9FE8] text-[#4A9FE8]'
                : `border-transparent ${t.muted} hover:${t.text}`
            }`}>
            {tab_ === 'scanner' ? `Fırsatlar (${onlyBoth ? bothCount : venueOpps.length})`
              : tab_ === 'active' ? `Açık Pair'lar (${activePairs.length})`
              : `Gruplar (${perpList.length})`}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto">

        {/* ── Scanner tab ─────────────────────────────────────────────────── */}
        {tab === 'scanner' && (
          <div>
            {/* Odak anahtarı — varsayılan AÇIK: yalnız funding VE basis'in aynı yönü
                gösterdiği çiftler. Kapatınca tam liste. */}
            <div className={`flex items-center gap-3 px-6 py-2.5 text-xs border-b ${t.border}`}>
              <button onClick={() => setOnlyBoth(v => !v)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${onlyBoth
                  ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-400'
                  : `${t.border} ${t.muted} hover:${t.text}`}`}>
                <span className={`w-8 h-4 rounded-full relative transition-colors ${onlyBoth ? 'bg-emerald-500/60' : 'bg-white/15'}`}>
                  <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${onlyBoth ? 'left-4' : 'left-0.5'}`} />
                </span>
                Sadece ikisi de kazandıranlar
              </button>
              <span className={t.muted}>
                {onlyBoth
                  ? `${bothCount} / ${venueOpps.length} çift — funding VE basis aynı yönde`
                  : `tam liste (${venueOpps.length})`}
              </span>
              <div className={`relative flex items-center rounded-lg border ${t.border}`}>
                <Search size={13} className={`absolute left-2 ${t.muted} pointer-events-none`} />
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') setQuery(''); }}
                  placeholder="parite ara — pump, deep sui"
                  className={`w-52 pl-7 pr-6 py-1.5 rounded-lg bg-transparent outline-none ${t.text}
                    placeholder:opacity-50 focus:border-[#4A9FE8]/60`} />
                {query && (
                  <button onClick={() => setQuery('')} title="Temizle (Esc)"
                    className={`absolute right-1.5 ${t.muted} hover:text-rose-400`}>
                    <X size={13} />
                  </button>
                )}
              </div>
              <span className={`ml-auto ${t.muted}`}>
                long index ALTINDAN alınır · short index ÜSTÜNDEN satılır
              </span>
            </div>

            {/* Borsa anahtarları — kapalı borsanın geçtiği çiftler tamamen gizlenir */}
            {venues.length > 0 && (
              <div className={`flex items-center gap-2 flex-wrap px-6 py-2 text-xs border-b ${t.border}`}>
                <span className={t.muted}>Borsalar:</span>
                {venues.map(([id, name]) => {
                  const on = !offVenues.has(id);
                  return (
                    <button key={id} onClick={() => toggleVenue(id)}
                      title={on ? 'Kapat' : 'Aç'}
                      className={`px-2.5 py-1 rounded-lg border transition-colors ${on
                        ? 'border-[#4A9FE8]/50 bg-[#4A9FE8]/15 text-[#4A9FE8]'
                        : `${t.border} ${t.muted} line-through opacity-60`}`}>
                      {name}
                    </button>
                  );
                })}
                {offVenues.size > 0 && (
                  <button onClick={() => setOffVenues(new Set())}
                    className={`ml-1 ${t.muted} hover:${t.text} underline`}>
                    hepsini aç
                  </button>
                )}
              </div>
            )}

            {shownOpps.length === 0 && !loading && (
              <div className={`flex flex-col items-center justify-center py-20 ${t.muted}`}>
                <Info size={32} className="mb-3 opacity-40" />
                <p className="text-sm">
                  {terms.length > 0 && queryOpps.length === 0
                    ? `"${query}" ile eşleşen parite yok.`
                    : venueOpps.length === 0 && offVenues.size > 0
                    ? 'Açık borsalarda fırsat yok — kapalı borsaları açmayı dene.'
                    : onlyBoth && venueOpps.length > 0
                      ? `${venueOpps.length} funding fırsatı var ama hiçbirinde basis aynı yönü göstermiyor.`
                      : "Fırsat bulunamadı. Botların ticker cache'i dolu mu?"}
                </p>
                {onlyBoth && venueOpps.length > 0 && (
                  <button onClick={() => setOnlyBoth(false)}
                    className={`mt-3 px-3 py-1.5 rounded-lg text-xs border ${t.border} ${t.muted}`}>
                    Tam listeyi göster
                  </button>
                )}
                <button onClick={fetchOpps} className={`mt-4 px-4 py-2 rounded-xl text-xs ${t.btnPrimary}`}>Tekrar Tara</button>
              </div>
            )}
            {loading && (
              <div className="flex items-center justify-center py-20">
                <RefreshCw size={20} className={`animate-spin ${t.muted}`} />
              </div>
            )}
            {shownOpps.length > 0 && (
              <table className="w-full text-xs">
                <thead className={`sticky top-0 ${dark ? 'bg-[#061530]' : 'bg-white'}`}>
                  <tr>
                    <th className={thCls} onClick={() => toggleSort('symbol')}>
                      <span className="flex items-center gap-1">Sembol <SortIcon field="symbol" /></span>
                    </th>
                    <th className={thCls}>Long Bot · Sembol</th>
                    <th className={thCls} onClick={() => toggleSort('long_funding')}
                      title="Borsanın kendi periyodundaki HAM oran; yanındaki rozet periyodu gösterir">
                      <span className="flex items-center gap-1">Long Funding <SortIcon field="long_funding" /></span>
                    </th>
                    <th className={thCls}>Long Bid / Ask</th>
                    <th className={thCls}>Short Bot · Sembol</th>
                    <th className={thCls} onClick={() => toggleSort('short_funding')}>
                      <span className="flex items-center gap-1">Short Funding <SortIcon field="short_funding" /></span>
                    </th>
                    <th className={thCls}>Short Bid / Ask</th>
                    <th className={thCls} onClick={() => toggleSort('net_funding')}>
                      <span className="flex items-center gap-1" title="Saatlik ortak paydada fark — periyotlar farklı olabildiği için ham çıkarma yanıltıcı olurdu">Net /saat <SortIcon field="net_funding" /></span>
                    </th>
                    <th className={thCls} title="Long bacağın ASK fiyatının kendi oracle'ına sapması. NEGATİF istiyoruz: index altından alım.">
                      Long basis
                    </th>
                    <th className={thCls} title="Short bacağın BID fiyatının kendi oracle'ına sapması. POZİTİF istiyoruz: index üstünden satış.">
                      Short basis
                    </th>
                    <th className={thCls} onClick={() => toggleSort('basis_edge')}
                      title="short basis − long basis. FUNDING YÖNÜ sinyali: her bacak kendi oracle'ına göre doğru taraftaysa funding yapısal olarak lehine akar. İcra edilebilir kazanç DEĞİL — onun için 'Giriş farkı' kolonuna bak.">
                      <span className="flex items-center gap-1">Oracle sinyali <SortIcon field="basis_edge" /></span>
                    </th>
                    <th className={thCls} onClick={() => toggleSort('exec_net_bps')}
                      title="ŞİMDİ iki bacağı da atsan ne kaparsın: (short bid − long ask)/long ask, iki taker komisyonu düşülmüş. Kararı bu sayı vermeli.">
                      <span className="flex items-center gap-1">Giriş farkı (net) <SortIcon field="exec_net_bps" /></span>
                    </th>
                    <th className={`${thCls} text-center`}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {shownOpps.map((o, i) => (
                    <tr key={i} className={`border-b ${t.tableRow} transition-colors`}>
                      <td className={`px-3 py-2.5 font-semibold ${t.text}`}>{o.symbol}</td>
                      <td className={`px-3 py-2.5 ${t.subtext}`}>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-blue-400 font-medium">{o.long_bot_name}</span>
                          <span className={t.muted}>{o.long_sym}</span>
                        </div>
                      </td>
                      <td className={`px-3 py-2.5 whitespace-nowrap ${o.long_funding >= 0 ? t.positive : t.negative}`}>
                        {fmtPct(o.long_funding)}
                        {(o as any).long_interval_h ? (
                          <span className={`ml-1.5 text-[10px] px-1 py-0.5 rounded ${t.border} border ${t.muted}`}
                            title="Bu paritede funding periyodu — borsa geneli değil, PARİTE bazında">
                            /{(o as any).long_interval_h}s
                          </span>
                        ) : null}
                        {/* Komisyon: basis'in başabaş noktası buna bağlı.
                            "varsayılan" işareti = hesaba özel oran alınamadı. */}
                        {(o as any).long_taker_fee != null && (
                          <div className={`text-[10px] mt-0.5 ${t.muted}`}
                            title={(o as any).long_fee_source === 'hesap'
                              ? 'Borsadan, bu hesaba özel (indirimler dahil)'
                              : 'Belgelenmiş genel oran — hesabın kademesi farklı olabilir'}>
                            M {Number((o as any).long_maker_fee).toFixed(3)}%
                            {' · '}T {Number((o as any).long_taker_fee).toFixed(3)}%
                            {(o as any).long_fee_source !== 'hesap' && (
                              <span className="text-amber-400/70"> ~</span>
                            )}
                            <BorrowAvail amount={(o as any).long_borrow_avail}
                              rate={(o as any).long_borrow_rate}
                              base={o.symbol} muted={t.muted} />
                          </div>
                        )}
                      </td>
                      <td className={`px-3 py-2.5`}>
                        <div className="group/leg relative">
                          <BidAskCell bid={o.long_bid} ask={o.long_ask} bidQty={(o as any).long_bid_qty} askQty={(o as any).long_ask_qty} dark={dark} />
                          {/* Emir tuşu YALNIZ işlem yapılabilen borsada. KuCoin gibi
                              veri-only botlarda (köprü yok) tuş gösterip anlaşılmaz
                              bağlantı hatası vermek yerine hiç göstermiyoruz. */}
                          {(o as any).long_can_trade === false ? (
                            <span className="absolute -right-1 top-1/2 -translate-y-1/2 opacity-0
                              group-hover/leg:opacity-100 transition-opacity px-1.5 py-0.5 rounded
                              border text-[10px] border-slate-500/40 text-slate-400"
                              title="Bu borsa yalnız veri kaynağı — emir yolu yok">
                              salt okunur
                            </span>
                          ) : (
                          <button
                            onClick={() => setLegOrder({
                              botId: o.long_bot, botName: o.long_bot_name, symbol: o.long_sym,
                              side: 'long',
                              price: Number(o.long_ask) || 0,
                              availQty: Number((o as any).long_ask_qty) || 0,
                              makerFee: Number((o as any).long_maker_fee) || 0,
                              takerFee: Number((o as any).long_taker_fee) || 0,
                              feeSource: (o as any).long_fee_source,
                              leverage: 5,
                              // Karşı bacak: hedge popup'ı bunlardan doldurulacak.
                              // Grup/SQLite beklemeden, doğrudan tarayıcı satırından.
                              base: o.symbol,
                              counterBotId: o.short_bot, counterSymbol: o.short_sym,
                            })}
                            title="Bu borsada LONG emri gönder"
                            className="absolute -right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover/leg:opacity-100
                              transition-opacity px-1.5 py-0.5 rounded border text-[10px]
                              border-[#4A9FE8]/50 bg-[#4A9FE8]/15 text-[#4A9FE8]">
                            ⊳
                          </button>
                          )}
                        </div>
                      </td>
                      <td className={`px-3 py-2.5 ${t.subtext}`}>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-blue-400 font-medium">{o.short_bot_name}</span>
                          <span className={t.muted}>{o.short_sym}</span>
                        </div>
                      </td>
                      <td className={`px-3 py-2.5 whitespace-nowrap ${o.short_funding >= 0 ? t.positive : t.negative}`}>
                        {fmtPct(o.short_funding)}
                        {(o as any).short_interval_h ? (
                          <span className={`ml-1.5 text-[10px] px-1 py-0.5 rounded ${t.border} border ${t.muted}`}
                            title="Bu paritede funding periyodu — borsa geneli değil, PARİTE bazında">
                            /{(o as any).short_interval_h}s
                          </span>
                        ) : null}
                        {/* Komisyon: basis'in başabaş noktası buna bağlı.
                            "varsayılan" işareti = hesaba özel oran alınamadı. */}
                        {(o as any).short_taker_fee != null && (
                          <div className={`text-[10px] mt-0.5 ${t.muted}`}
                            title={(o as any).short_fee_source === 'hesap'
                              ? 'Borsadan, bu hesaba özel (indirimler dahil)'
                              : 'Belgelenmiş genel oran — hesabın kademesi farklı olabilir'}>
                            M {Number((o as any).short_maker_fee).toFixed(3)}%
                            {' · '}T {Number((o as any).short_taker_fee).toFixed(3)}%
                            {(o as any).short_fee_source !== 'hesap' && (
                              <span className="text-amber-400/70"> ~</span>
                            )}
                            <BorrowAvail amount={(o as any).short_borrow_avail}
                              rate={(o as any).short_borrow_rate}
                              base={o.symbol} muted={t.muted} />
                          </div>
                        )}
                      </td>
                      <td className={`px-3 py-2.5`}>
                        <div className="group/leg relative">
                          <BidAskCell bid={o.short_bid} ask={o.short_ask} bidQty={(o as any).short_bid_qty} askQty={(o as any).short_ask_qty} dark={dark} />
                          {(o as any).short_can_trade === false ? (
                            <span className="absolute -right-1 top-1/2 -translate-y-1/2 opacity-0
                              group-hover/leg:opacity-100 transition-opacity px-1.5 py-0.5 rounded
                              border text-[10px] border-slate-500/40 text-slate-400"
                              title="Bu borsa yalnız veri kaynağı — emir yolu yok">
                              salt okunur
                            </span>
                          ) : (
                          <button
                            onClick={() => setLegOrder({
                              botId: o.short_bot, botName: o.short_bot_name, symbol: o.short_sym,
                              side: 'short',
                              price: Number(o.short_bid) || 0,
                              availQty: Number((o as any).short_bid_qty) || 0,
                              makerFee: Number((o as any).short_maker_fee) || 0,
                              takerFee: Number((o as any).short_taker_fee) || 0,
                              feeSource: (o as any).short_fee_source,
                              leverage: 5,
                              base: o.symbol,
                              counterBotId: o.long_bot, counterSymbol: o.long_sym,
                            })}
                            title="Bu borsada SHORT emri gönder"
                            className="absolute -right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover/leg:opacity-100
                              transition-opacity px-1.5 py-0.5 rounded border text-[10px]
                              border-[#4A9FE8]/50 bg-[#4A9FE8]/15 text-[#4A9FE8]">
                            ⊳
                          </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`font-bold ${o.net_funding > 0.0001 ? 'text-emerald-400' : t.muted}`}>
                          {fmtPct(o.net_funding)}
                        </span>
                      </td>
                      {/* Basis: renk YÖNE göre — long'da negatif iyi, short'ta pozitif iyi.
                          null = oracle/derinlik yok; sıfır varsaymıyoruz, "—" gösteriyoruz. */}
                      <td className="px-3 py-2.5 tabular-nums">
                        {(o as any).long_basis == null ? <span className={t.muted}>—</span> : (
                          <span className={(o as any).long_basis < 0 ? 'text-emerald-400' : 'text-rose-400'}>
                            {((o as any).long_basis).toFixed(3)}%
                          </span>)}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums">
                        {(o as any).short_basis == null ? <span className={t.muted}>—</span> : (
                          <span className={(o as any).short_basis > 0 ? 'text-emerald-400' : 'text-rose-400'}>
                            {((o as any).short_basis).toFixed(3)}%
                          </span>)}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums">
                        {(o as any).basis_edge == null ? <span className={t.muted}>—</span> : (
                          <span className={(o as any).basis_edge > 0 ? 'text-emerald-400/80' : 'text-rose-400/80'}>
                            {((o as any).basis_edge).toFixed(3)}%
                          </span>)}
                        {/* İki oracle ayrışmışsa yukarıdaki üç basis sayısı da güvenilmez:
                            ONE'da ölçüldü — edge %−0,312 iken index farkı %−0,313, aynı sayı. */}
                        {Math.abs(Number((o as any).index_gap_pct) || 0) > 0.2 && (
                          <span className="ml-1 text-[10px] px-1 rounded border border-amber-500/40 text-amber-400"
                            title={`İki borsanın index'i %${Number((o as any).index_gap_pct).toFixed(3)} ayrışmış — bu sütundaki basis sayıları güvenilmez, biri bayat ya da farklı sepetle hesaplanıyor olabilir.`}>
                            idx {Number((o as any).index_gap_pct).toFixed(2)}%
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums">
                        {(o as any).exec_net_bps == null ? <span className={t.muted}>—</span> : (
                          <span className={`font-bold ${(o as any).exec_net_bps > 0 ? 'text-emerald-400' : 'text-rose-400'}`}
                            title={`ham ${Number((o as any).exec_bps).toFixed(1)} bps − komisyon ${Number((o as any).exec_cost_bps).toFixed(1)} bps`}>
                            {Number((o as any).exec_net_bps) > 0 ? '+' : ''}{Number((o as any).exec_net_bps).toFixed(1)} bps
                          </span>)}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <button
                          onClick={() => setOpenModal(o)}
                          className="flex items-center gap-1 mx-auto px-2.5 py-1 rounded-lg bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 text-xs font-medium transition-colors">
                          <Play size={11} /> Aç
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Active pairs tab ─────────────────────────────────────────────── */}
        {tab === 'active' && (
          <div>
            <div className="flex items-center gap-3 px-4 py-2 border-b border-transparent">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={showClosed}
                  onChange={e => setShowClosed(e.target.checked)}
                  className="rounded"
                />
                <span className={t.muted}>Kapalı pair'ları göster</span>
              </label>
            </div>

            {displayPairs.length === 0 && (
              <div className={`flex flex-col items-center justify-center py-20 ${t.muted}`}>
                <Info size={32} className="mb-3 opacity-40" />
                <p className="text-sm">Henüz açık pair yok.</p>
                <button onClick={() => setTab('scanner')} className={`mt-4 px-4 py-2 rounded-xl text-xs ${t.btnPrimary}`}>
                  Fırsatları Tara
                </button>
              </div>
            )}

            {displayPairs.length > 0 && (
              <table className="w-full text-xs">
                <thead className={`sticky top-0 ${dark ? 'bg-[#061530]' : 'bg-white'}`}>
                  <tr>
                    <th className={thCls}>Sembol</th>
                    <th className={thCls}>Mod</th>
                    <th className={thCls}>Long</th>
                    <th className={thCls}>Short</th>
                    <th className={thCls}>Büyüklük</th>
                    <th className={thCls}>Açılış Fiyatı</th>
                    <th className={thCls}>Mark P&L</th>
                    <th className={thCls}>Fund. Gelir (est)</th>
                    <th className={thCls}>Delta</th>
                    <th className={thCls}>Net Fund / 8h</th>
                    <th className={thCls}>Süre</th>
                    <th className={thCls}>Durum</th>
                    <th className={`${thCls} text-center`}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {displayPairs.map(pair => {
                    const isActive  = pair.status === 'active';
                    const isClosing = pair.status === 'closing';
                    const isClosed  = pair.status === 'closed';
                    const netNow    = pair.short_funding_now - pair.long_funding_now;
                    const totalPnl  = pair.sim_pnl_mark + pair.sim_funding_est;

                    return (
                      <tr key={pair.id} className={`border-b ${t.tableRow} ${isClosed ? 'opacity-50' : ''} transition-colors`}>
                        <td className={`px-3 py-2.5 font-semibold ${t.text}`}>{pair.symbol}</td>
                        <td className="px-3 py-2.5">
                          {pair.sim_mode
                            ? <span className="text-blue-400 border border-blue-400/40 px-1.5 py-0.5 rounded text-[10px]">SIM</span>
                            : <span className="text-emerald-400 border border-emerald-400/40 px-1.5 py-0.5 rounded text-[10px]">LIVE</span>}
                        </td>
                        <td className={`px-3 py-2.5 ${t.subtext}`}>
                          <div className="text-blue-400 font-medium">{pair.long_bot_name || pair.long_bot}</div>
                          <div className={t.muted}>{pair.long_sym}</div>
                          <div className={`${pair.long_funding_now >= 0 ? t.positive : t.negative} text-[10px]`}>
                            {fmtPct(pair.long_funding_now)}
                          </div>
                        </td>
                        <td className={`px-3 py-2.5 ${t.subtext}`}>
                          <div className="text-blue-400 font-medium">{pair.short_bot_name || pair.short_bot}</div>
                          <div className={t.muted}>{pair.short_sym}</div>
                          <div className={`${pair.short_funding_now >= 0 ? t.positive : t.negative} text-[10px]`}>
                            {fmtPct(pair.short_funding_now)}
                          </div>
                        </td>
                        <td className={`px-3 py-2.5 ${t.text}`}>{fmt(pair.size, 4)}</td>
                        <td className={`px-3 py-2.5 ${t.subtext}`}>
                          <div>L: ${fmt(pair.long_entry, 2)}</div>
                          <div>S: ${fmt(pair.short_entry, 2)}</div>
                        </td>
                        <td className={`px-3 py-2.5 font-medium ${pair.sim_pnl_mark >= 0 ? t.positive : t.negative}`}>
                          {pair.sim_pnl_mark >= 0 ? '+' : ''}{fmt(pair.sim_pnl_mark, 4)} USDC
                        </td>
                        <td className={`px-3 py-2.5 font-medium ${pair.sim_funding_est >= 0 ? t.positive : t.negative}`}>
                          {pair.sim_funding_est >= 0 ? '+' : ''}{fmt(pair.sim_funding_est, 4)} USDC
                        </td>
                        <td className={`px-3 py-2.5 ${Math.abs(pair.delta_usd ?? 0) < 1 ? t.positive : t.negative}`}>
                          {pair.delta_usd !== undefined ? `$${fmt(pair.delta_usd, 2)}` : '—'}
                        </td>
                        <td className={`px-3 py-2.5 font-medium ${netNow > 0 ? t.positive : t.negative}`}>
                          {fmtPct(netNow)}
                        </td>
                        <td className={`px-3 py-2.5 ${t.muted}`}>{age(pair.opened_at)}</td>
                        <td className="px-3 py-2.5">
                          {isActive  && <span className="text-emerald-400">● Aktif</span>}
                          {isClosing && <span className="text-amber-400">● Kapanıyor</span>}
                          {isClosed  && <span className="text-slate-500">● Kapalı</span>}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          {isActive && (
                            <button
                              onClick={() => handleClose(pair.id)}
                              className="flex items-center gap-1 mx-auto px-2.5 py-1 rounded-lg bg-rose-600/20 hover:bg-rose-600/40 text-rose-400 text-xs font-medium transition-colors">
                              <Square size={11} /> Kapat
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Summary row */}
            {activePairs.length > 0 && (
              <div className={`mt-2 mx-4 mb-4 p-3 rounded-xl ${dark ? 'bg-[#061530]/60' : 'bg-[#E8EEF7]'} flex flex-wrap gap-6 text-xs`}>
                <div>
                  <span className={t.muted}>Toplam Mark P&L: </span>
                  <span className={`font-bold ${activePairs.reduce((s, p) => s + p.sim_pnl_mark, 0) >= 0 ? t.positive : t.negative}`}>
                    {activePairs.reduce((s, p) => s + p.sim_pnl_mark, 0) >= 0 ? '+' : ''}
                    {fmt(activePairs.reduce((s, p) => s + p.sim_pnl_mark, 0), 4)} USDC
                  </span>
                </div>
                <div>
                  <span className={t.muted}>Toplam Fund. Gelir: </span>
                  <span className="font-bold text-emerald-400">
                    +{fmt(activePairs.reduce((s, p) => s + p.sim_funding_est, 0), 4)} USDC
                  </span>
                </div>
                <div>
                  <span className={t.muted}>Net Toplam: </span>
                  <span className={`font-bold ${
                    activePairs.reduce((s, p) => s + p.sim_pnl_mark + p.sim_funding_est, 0) >= 0 ? t.positive : t.negative
                  }`}>
                    {activePairs.reduce((s, p) => s + p.sim_pnl_mark + p.sim_funding_est, 0) >= 0 ? '+' : ''}
                    {fmt(activePairs.reduce((s, p) => s + p.sim_pnl_mark + p.sim_funding_est, 0), 4)} USDC
                  </span>
                </div>
                <div>
                  <span className={t.muted}>Açık Pair: </span>
                  <span className={`font-bold ${t.text}`}>{activePairs.length}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Gruplar tab: cross-exchange funding arb takibi ──────────────── */}
        {tab === 'groups' && (
          <div className="p-6">
            {/* Yaşayan / Kapalı + yeni grup */}
            <div className="flex items-center gap-2 mb-4">
              {([['living', 'Yaşayan'], ['closed', 'Kapalı']] as const).map(([k, label]) => {
                const n = perpList.filter(g => (g.status || 'living') === k).length;
                return (
                  <button key={k} onClick={() => setGTab(k)}
                    className={`px-3 py-1.5 rounded-lg text-xs border transition ${gTab === k
                      ? 'border-[#4A9FE8] text-[#4A9FE8] bg-[#4A9FE8]/10'
                      : `${t.border} ${t.muted} hover:text-[#4A9FE8]`}`}>
                    {label} ({n})
                  </button>
                );
              })}
              <button onClick={() => sendWs({ type: 'create_lp_group', payload: { kind: 'perp' } })}
                className={`ml-auto px-3 py-1.5 rounded-lg text-xs font-semibold ${t.btnPrimary}`}>
                + Yeni grup
              </button>
            </div>

            {/* Sekme toplamı — tek tek kartları toplamak zorunda kalma.
                Sadece GÖRÜNEN sekmenin grupları toplanır: kapalı sekmesindeki rakam
                gerçekleşmiş sonucu, yaşayan sekmesindeki devam eden pozisyonu anlatır. */}
            {(() => {
              const shown = perpList.filter(g => (g.status || 'living') === gTab);
              if (!shown.length) return null;
              const sum = (f: (d: any) => number) => shown.reduce(
                (a, g) => a + (Number(f(livePerpGroups[String(g.no)] || {})) || 0), 0);
              const basisT = sum(d => d.basisUsd ?? d.realizedPnl ?? 0);
              const fundT  = sum(d => d.fundingUsd);
              const feeT   = sum(d => d.fee);
              const gasT   = sum(d => d.gasUsd);
              const openT  = sum(d => d.openPnl);
              const netT   = sum(d => d.netUsd);
              const cell = (label: string, v: number, hint?: string) => (
                <div title={hint}>
                  <div className={`${t.muted} text-[10px]`}>{label}</div>
                  <div className={`text-sm font-semibold tabular-nums ${v >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {v >= 0 ? '' : '−'}${Math.abs(v).toFixed(4)}
                  </div>
                </div>
              );
              return (
                <div className={`flex items-center gap-6 flex-wrap rounded-lg border ${t.border} px-4 py-3 mb-4`}>
                  <div>
                    <div className={`${t.muted} text-[10px]`}>
                      {gTab === 'closed' ? 'Kapalı gruplar' : 'Yaşayan gruplar'}
                    </div>
                    <div className={`text-sm font-semibold ${t.text}`}>{shown.length} grup</div>
                  </div>
                  <div className={`h-8 w-px ${t.border} border-l`} />
                  {cell('Net', netT, 'Funding + fiyat farkı − komisyon (+ açık kısmın kağıt PnL’i)')}
                  {cell('Funding', fundT)}
                  {cell('Fiyat farkı', basisT, 'Grubun kendi işlemlerinden gerçekleşmiş PnL')}
                  {Math.abs(openT) > 1e-9 &&
                    cell('Açık kısım (kağıt)', openT, 'Henüz gerçekleşmedi — grubun kendi ortalaması mark fiyatına vuruldu')}
                  <div>
                    <div className={`${t.muted} text-[10px]`}>Komisyon</div>
                    <div className="text-sm font-semibold tabular-nums text-rose-400">−${feeT.toFixed(4)}</div>
                  </div>
                  {gasT > 0 && (
                    <div title="Zincir üstü gaz — komisyondan ayrı ama net'e dahil">
                      <div className={`${t.muted} text-[10px]`}>Gaz</div>
                      <div className="text-sm font-semibold tabular-nums text-rose-400">−${gasT.toFixed(4)}</div>
                    </div>
                  )}
                </div>
              );
            })()}

            {perpList.filter(g => (g.status || 'living') === gTab).length === 0 && (
              <div className={`text-center py-12 text-sm ${t.muted}`}>
                {gTab === 'living'
                  ? <>Grup yok. <span className="text-[#4A9FE8]">+ Yeni grup</span> ile açıp, İşlem Geçmişi sayfasından işlemleri gruba etiketle.</>
                  : 'Kapalı grup yok.'}
              </div>
            )}

            <div className="space-y-3">
              {perpList.filter(g => (g.status || 'living') === gTab).map(g => {
                const d = livePerpGroups[String(g.no)];
                const legs = d?.legs || [];
                return (
                  <div key={g.no} className={`rounded-xl border ${t.border} ${t.card} p-4`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-sm font-semibold ${t.text}`}>Grup {g.no}</span>
                      <span className={`text-[11px] ${t.muted}`}>{g.trades} işlem · {legs.length} bacak</span>
                      {/* Karşı bacağı tamamla — en büyük bacağı referans alır, KARŞI
                          yönü ve aynı miktarı ön-doldurur. Motor tavanı aşarsa kovalamaz. */}
                      {legs.length > 0 && (
                        <button
                          onClick={() => {
                            // Referans: mutlak büyüklüğü en fazla olan bacak
                            const ref = [...legs].sort((a: any, b: any) =>
                              Math.abs(b.netQty || 0) - Math.abs(a.netQty || 0))[0] as any;
                            const sides = ref.sides || {};
                            const dom = (sides.LONG?.qty || 0) >= (sides.SHORT?.qty || 0) ? 'LONG' : 'SHORT';
                            const sv = sides[dom] || {};
                            const other = legs.find((l: any) => l.botId !== ref.botId) as any;
                            // TEK BACAKLI grupta `other` yok. Eskiden ref'in KENDİSİNE
                            // düşüyordu: hedef borsa = açık bacağın borsası, yani motor
                            // hedge değil POZİSYON KAPATMA yapıyordu (Extended UNI-USD'de
                            // yaşandı). Artık boş bırakılıyor; borsayı kullanıcı seçiyor
                            // ve seçilene kadar Başlat kapalı.
                            // Karşı bacak: önce gerçek ikinci bacak, yoksa grup
                            // açılırken kaydedilen HEDEF (tarayıcıdaki ⊳ ile açıldıysa).
                            // Eskiden ref'in kendisine düşüyordu → hedge yerine kapatma.
                            const rawSym = other?.symbol || (g as any).hedgeSymbol || '';
                            setHedge({
                              groupNo: g.no,
                              symbol: rawSym,
                              base: canonicalBase(other?.symbol || (g as any).hedgeSymbol || ref.symbol),
                              side: dom === 'LONG' ? 'short' : 'long',
                              quantity: Number(sv.qty || Math.abs(ref.netQty) || 0),
                              refPrice: Number(sv.avgPrice || ref.avgEntry || 0),
                              refLabel: `${ref.botId} · ${dom} ${Number(sv.qty || 0)
                                .toLocaleString('en-US')} @ ${Number(sv.avgPrice || 0).toFixed(6)}`,
                              botId: other?.botId || (g as any).hedgeBot || '',
                              selfBotId: ref.botId,
                            });
                          }}
                          className="ml-auto text-[11px] px-2.5 py-1 rounded-lg border border-amber-500/45 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20">
                          Karşı bacağı tamamla
                        </button>
                      )}
                      {/* İKİ bacaklı grupta kademeli büyütme/azaltma. Tek bacakta
                          anlamsız — önce karşı bacak tamamlanmalı. */}
                      {legs.length >= 2 && (() => {
                        const [x, y] = legs as any[];
                        const sideOf = (l: any): 'long' | 'short' => (l.netQty >= 0 ? 'long' : 'short');
                        if (sideOf(x) === sideOf(y)) return null;   // delta-nötr değil
                        return (
                          <button
                            onClick={() => setScale({
                              groupNo: g.no,
                              legA: { botId: x.botId, symbol: x.symbol, side: sideOf(x) },
                              legB: { botId: y.botId, symbol: y.symbol, side: sideOf(y) },
                              currentSize: Math.min(Math.abs(x.netQty || 0), Math.abs(y.netQty || 0)),
                              tickSize: Number(x.tickSize) || 0,
                            })}
                            title="Fark yeterliyken kliplerle büyüt ya da azalt"
                            className="text-[11px] px-2.5 py-1 rounded-lg border border-emerald-500/45 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20">
                            Pozisyon artır
                          </button>
                        );
                      })()}
                      <button
                        onClick={() => sendWs({ type: 'set_lp_group_status',
                          payload: { groupNo: g.no, status: (g.status === 'closed' ? 'living' : 'closed') } })}
                        className={`${legs.length > 0 ? '' : 'ml-auto '}text-[11px] px-2.5 py-1 rounded-lg border ${t.border} ${t.muted} hover:text-[#4A9FE8]`}>
                        {g.status === 'closed' ? 'Aç' : 'Kapat'}
                      </button>
                    </div>

                    {legs.length === 0 ? (
                      <div className={`text-xs ${t.muted}`}>Henüz işlem etiketlenmemiş.</div>
                    ) : (
                      <div className="flex items-start gap-4">
                        {/* Bacaklar */}
                        <div className="flex-1 min-w-0 space-y-1.5">
                          {legs.map((l: any, i: number) => {
                            // netQty long−short nettir: round-trip kapandığında 0 çıkar ve
                            // "0 @ 0.000000" görünürdü. Tarafları AYRI göstermek miktarı ve
                            // ortalama fiyatı her durumda okunur kılıyor.
                            const sides = l.sides || {};
                            const order = ['LONG', 'SHORT'].filter(k => sides[k]?.qty);
                            const open  = Math.abs(l.netQty || 0) > 1e-9;
                            const nQ = (v: number, d = 4) =>
                              Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: d });
                            // NOMİNAL: miktar tek başına kıyaslanamıyor — 2.222.220 ONE ile
                            // 0,5 HYPE aynı satırda dururken hangi bacağın daha büyük
                            // olduğu görünmüyor. Mark yoksa açık ortalamaya düş; ikisi de
                            // yoksa hiç gösterme (0 yazmak yanlış bilgi olur).
                            const openQtyAbs = Math.abs(l.netQty || 0);
                            const legPx  = Number(l.markPrice || 0) || Number(l.openAvg || 0);
                            const legUsd = legPx > 0 ? openQtyAbs * legPx : null;
                            return (
                              <div key={i} className={`text-xs px-2.5 py-2 rounded-lg border ${t.border}`}>
                                {/* Bacak başlığı — sembol, bot, açık/kapalı, funding */}
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`font-semibold shrink-0 ${t.text}`}>
                                    {(l.symbol || '').replace('PERP_', '').replace('_USDC', '').replace('-PERP', '')}
                                  </span>
                                  <span className={`text-[11px] shrink-0 ${t.muted}`}>{l.botId}</span>
                                  {open ? (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${l.netQty > 0
                                      ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                                      : 'bg-rose-500/15 text-rose-400 border-rose-500/30'}`}>
                                      açık {l.netQty > 0 ? 'LONG' : 'SHORT'} {nQ(openQtyAbs)}
                                    </span>
                                  ) : (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${t.muted}`}>kapalı</span>
                                  )}
                                  {open && legUsd != null && (
                                    <span className={`text-[10px] tabular-nums shrink-0 ${t.muted}`}
                                      title={`nominal = ${nQ(openQtyAbs)} × ${legPx} (${Number(l.markPrice || 0) > 0 ? 'mark' : 'açık ortalama'})`}>
                                      ${legUsd.toLocaleString('en-US', { maximumFractionDigits: legUsd >= 1000 ? 0 : 2 })}
                                    </span>
                                  )}
                                  <span className={`ml-auto tabular-nums shrink-0 ${l.fundingUsd >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    funding ${l.fundingUsd?.toFixed(4)}
                                  </span>
                                  {/* Bu bacağın AÇIK olduğu borsadaki güncel oran ve
                                      sonraki ödeme anı — pozisyon açıkken hangi tarafın
                                      ne zaman ödeyeceğini görmeden karar vermek zor. */}
                                  {open && (
                                    <span className="flex items-center gap-1.5 shrink-0 text-[10px]">
                                      {l.fundingRate != null && (
                                        <span className={`tabular-nums ${Number(l.fundingRate) >= 0
                                          ? 'text-emerald-400/80' : 'text-rose-400/80'}`}
                                          title="o borsadaki güncel funding oranı (periyot başına)">
                                          {Number(l.fundingRate) >= 0 ? '+' : ''}{Number(l.fundingRate).toFixed(4)}%
                                        </span>
                                      )}
                                      <FundingIn ts={Number(l.nextFunding) || 0} muted={t.muted} />
                                    </span>
                                  )}
                                  {/* GRUBUN KENDİ kağıt PnL'i. Borsanın unrealizedPnl'i
                                      HESABIN tamamı için — grup dışı pozisyon varsa
                                      (Bluefin-Phantom'daki grup dışı 25.000 DEEP gibi)
                                      o rakam bu kartla ilgisiz bir sayı gösteriyordu.
                                      Artık yalnız bu grubun açık kısmı: (mark−ort)×miktar. */}
                                  {open && (
                                    <span className={`tabular-nums shrink-0 text-[11px] ${Number(l.openPnl || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}
                                      title={`Grubun kendi açık kısmı: (${Number(l.markPrice || 0)} − ${Number(l.openAvg || 0)}) × ${Number(l.openQty || 0)}`}>
                                      kağıt ${Number(l.openPnl || 0).toFixed(2)}
                                    </span>
                                  )}
                                  {/* Kapatma tuşları — emir GÖNDERMEZ, popup'ı doldurup açar.
                                      Miktar düzenlenebilir: 75.000'i 15.000×5 kapatmak için. */}
                                  {open && (() => {
                                    const req = {
                                      botId: l.botId, symbol: l.symbol, groupNo: g.no,
                                      openSide: (l.netQty > 0 ? 'long' : 'short') as 'long' | 'short',
                                      openQty: Math.abs(l.netQty || 0),
                                    };
                                    const ask = (maker: boolean) => {
                                      setCloseReq({ ...req, maker });
                                      sendWs({ type: 'get_leg_quote',
                                        payload: { botId: l.botId, symbol: l.symbol } });
                                    };
                                    const busy = closeReq?.botId === l.botId && closeReq?.symbol === l.symbol;
                                    return (
                                      <span className="flex items-center gap-1 shrink-0">
                                        <button onClick={() => ask(true)} disabled={busy}
                                          title="Kendi tarafına post-only yaz — düşük komisyon, dolmayabilir"
                                          className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15 disabled:opacity-40">
                                          maker kapat
                                        </button>
                                        <button onClick={() => ask(false)} disabled={busy}
                                          title="Karşı tarafı geç — hemen kapanır, taker komisyonu"
                                          className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/15 disabled:opacity-40">
                                          taker kapat
                                        </button>
                                      </span>
                                    );
                                  })()}
                                </div>

                                {/* Taraf kırılımı: miktar @ ortalama fiyat */}
                                <div className="mt-1 space-y-0.5">
                                  {order.length === 0 && (
                                    <div className={`text-[11px] ${t.muted}`}>işlem detayı yok</div>
                                  )}
                                  {order.map(sd => {
                                    const v = sides[sd];
                                    return (
                                      <div key={sd} className="flex items-center gap-2 text-[11px]">
                                        <span className={`font-semibold w-12 shrink-0 ${sd === 'LONG' ? 'text-emerald-400' : 'text-rose-400'}`}>{sd}</span>
                                        <span className={`tabular-nums ${t.text}`}>{nQ(v.qty)}</span>
                                        <span className={t.muted}>@</span>
                                        <span className={`tabular-nums ${t.text}`}>{Number(v.avgPrice || 0).toFixed(6)}</span>
                                        <span className={`${t.muted} ml-2`}>{v.count} işlem</span>
                                        {v.fee > 0 && <span className={t.muted}>· fee ${Number(v.fee).toFixed(4)}</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Sonuç kartı */}
                        <div className={`shrink-0 w-52 rounded-lg border ${t.border} p-3 text-[11px] space-y-2`}>
                          {(() => {
                            // Kırılım: kârın ne kadarı TEKRARLAYAN funding'den, ne kadarı
                            // FIRSATÇI fiyat farkından geldi. Ölçeklerken bu ayrım önemli.
                            const fee0  = Number(d.fee || 0);
                            const gas0  = Number(d.gasUsd || 0);
                            const openP0 = Number(d.openPnl || 0);
                            const basis0 = Number(d.basisUsd ?? d.realizedPnl ?? 0);
                            const fund0  = Number(d.fundingUsd || 0);
                            // PAYDA NET DEĞİL, BRÜT HAREKET. Net'e bölmek çalışan bir
                            // delta-nötr grupta çöp üretiyordu: net sıfıra yakın olduğu
                            // için pay patlıyor ve işaret ters dönüyor — grup 69'da
                            // funding +$5,13 iken "%-448" yazıyordu (net −$1,15).
                            // Bileşenlerin mutlak toplamına bölünce her kalem 0-100
                            // arasında kalıyor ve "hangi kalem ne kadar oynattı"
                            // sorusunu gerçekten yanıtlıyor.
                            const gross = Math.abs(fund0) + Math.abs(basis0)
                                        + Math.abs(openP0) + Math.abs(fee0) + Math.abs(gas0);
                            const pct = (v: number) => (gross > 1e-9
                              ? `%${Math.round(100 * v / gross)}` : '—');
                            // basisUsd: grubun KENDİ işlemlerinden (borsa bildirimi değil).
                            // Eskiden d.realizedPnl okunuyordu; Extended realized PnL
                            // bildirmediği için kazanan bacak görünmez oluyor ve işaret
                            // ters dönüyordu (grup 12: −$11.28 yerine gerçek +$2.49).
                            const basis = basis0;
                            const openP = openP0;
                            const fund  = fund0;
                            const apr   = d.fundingApr;
                            const isBasis = d.pnlKind === 'basis';
                            const hrs = d.durationMs ? d.durationMs / 3600000 : 0;
                            return (
                              <>
                                <div>
                                  <div className={`${t.muted} text-[10px] flex items-center gap-1`}>
                                    Funding <span className="opacity-60"
                                      title="Grubun brüt hareketindeki payı (funding + basis + kağıt + komisyon mutlak toplamı = %100)">{pct(fund)}</span>
                                  </div>
                                  <div className={`text-base font-semibold tabular-nums ${fund >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    ${fund.toFixed(4)}
                                  </div>
                                  {apr != null && (
                                    <div className={`text-[10px] tabular-nums ${apr >= 0 ? 'text-emerald-400/80' : 'text-rose-400/80'}`}
                                      title={`Zaman-ağırlıklı nominal $${Number(d.avgNotional || 0).toLocaleString('en-US')} üzerinden, ${hrs.toFixed(1)} saatlik tutuş yıllığa çevrildi`}>
                                      APR %{Number(apr).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                    </div>
                                  )}
                                </div>

                                <div className={`pt-2 border-t ${t.border}`}>
                                  <div className={`${t.muted} text-[10px] flex items-center gap-1`}>
                                    {isBasis ? 'Basis (borsa farkı)' : 'Fiyat PnL (yönlü)'}
                                    <span className="opacity-60"
                                      title="Grubun brüt hareketindeki payı (funding + basis + kağıt + komisyon mutlak toplamı = %100)">{pct(basis)}</span>
                                  </div>
                                  <div className={`tabular-nums ${basis >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    ${basis.toFixed(4)}
                                  </div>
                                  {/* Hedge nötrlüğü "basis" etiketinin dayanağı — göstermezsek
                                      yönlü bir grubun kârı arbitraj sanılabilir. */}
                                  <div className={`text-[10px] tabular-nums ${isBasis ? t.muted : 'text-amber-400/90'}`}
                                    title={isBasis
                                      ? 'Süre boyunca delta-nötr kalındı → fiyat hareketi bacaklarda götürüldü, kalan borsalar arası fark'
                                      : 'Grup delta-nötr değildi (tek bacak ya da açık pozisyon) → bu kâr yönlü risk taşıyor'}>
                                    {isBasis ? `nötr %${d.neutralPct}` : 'hedge yok — yönlü'}
                                  </div>
                                  {Math.abs(openP) > 1e-9 && (
                                    <div className={`text-[10px] tabular-nums ${openP >= 0 ? 'text-emerald-400/80' : 'text-rose-400/80'}`}
                                      title="Grubun HÂLÂ AÇIK kısmı — grubun kendi ortalaması mark fiyatına vurularak hesaplandı. Henüz gerçekleşmedi.">
                                      açık kısım (kağıt) ${openP.toFixed(4)} <span className="opacity-60">{pct(openP)}</span>
                                    </div>
                                  )}
                                  <div className={`${t.muted} text-[10px] tabular-nums`}>
                                    komisyon −${fee0.toFixed(4)} <span className="opacity-60">{pct(-fee0)}</span>
                                  </div>
                                  {Number(d.gasUsd || 0) > 0 && (
                                    <div className={`${t.muted} text-[10px] tabular-nums`}
                                      title="Zincir üstü gaz (Bluefin işlem başına ~$0.03). Komisyondan ayrı ama net'e dahil.">
                                      gaz −${Number(d.gasUsd).toFixed(4)}
                                    </div>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                          <div className={`pt-2 border-t ${t.border}`}>
                            <div className={`${t.muted} text-[10px]`}>Net</div>
                            <div className={`text-base font-bold tabular-nums ${d.netUsd >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                              ${d.netUsd?.toFixed(4)}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {legOrder && (
        <LegOrderModal dark={dark} prefill={legOrder}
          sendWs={sendWs as any}
          sendForBot={sendForBot as any} subscribe={subscribe as any}
          onHedge={({ groupNo, quantity, refPrice }) => {
            // Hedge popup'ı GRUPTAN değil, az önceki emirden dolduruluyor —
            // SQLite'ı beklemenin tek sebebi muhasebeydi, icrayı bloke etmemeli.
            const lo = legOrder as any;
            setHedge({
              groupNo,
              symbol: lo.counterSymbol,
              base: lo.base || canonicalBase(lo.counterSymbol),
              side: lo.side === 'long' ? 'short' : 'long',
              quantity,
              refPrice,
              refLabel: `${lo.botName} · ${String(lo.side).toUpperCase()} `
                + `${quantity.toLocaleString('en-US')} @ ${refPrice}`,
              botId: lo.counterBotId,
            });
            setLegOrder(null);
          }}
          onClose={() => setLegOrder(null)} />
      )}

      {scale && (
        <ScaleModal dark={dark} prefill={scale} sendWs={sendWs as any}
          subscribe={subscribe as any} onClose={() => setScale(null)} />
      )}

      {hedge && (
        <HedgeCompleteModal
          dark={dark} prefill={hedge}
          bots={venues.map(([id, name]) => ({ id, name }))}
          symbolFor={symbolFor}
          sendWs={sendWs} subscribe={subscribe as any}
          onClose={() => setHedge(null)} />
      )}

      {/* Open pair modal */}
      {openModal && (
        <OpenPairModal
          dark={dark}
          opp={openModal}
          onClose={() => setOpenModal(null)}
          onOpen={handleOpen}
        />
      )}
    </div>
  );
}
